import React, { useState, useEffect, useRef } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { doc, getDoc, updateDoc, serverTimestamp, collection, query, where, getDocs, addDoc, onSnapshot } from "firebase/firestore";
import { storage, ref, uploadBytes, getDownloadURL, getNextMocNumber, sendEmailNotification } from './firebaseUtils.js';
import Spinner from './Spinner.js';
import { ArrowLeft, Edit2, Save, Printer, Truck, ClipboardCheck, UserCheck, Clock, FileText } from 'lucide-react';
import QCPreview from './QCPreview.js';
import HorizontalTimeline from './components/HorizontalTimeline.js';
import MultiFileUpload from './components/MultiFileUpload.js';
import StatusBadge from './components/StatusBadge.js';
import { useConfig } from './hooks/useConfig.js';
import { isWarehouseUser } from './accessControl.js';

const qcChecklistItems = [
  { key: 'qualityChecked', label: 'Material Quality Checked' },
  { key: 'qtyVerified', label: 'Quantity Verified vs BOM' },
  { key: 'properPacking', label: 'Proper Packing & Wrapping' },
  { key: 'laserMarking', label: 'Laser Marking Verified' },
  { key: 'stickersDone', label: 'Stickers Applied' },
  { key: 'docsHanded', label: 'Documents Handed to Driver' },
  { key: 'safetyTag', label: 'Safety Tags Attached' },
  { key: 'informedHO', label: 'Informed HO/Sales' }
];

const dispatchRequiredFields = [
  { key: 'vehicleType', label: 'Vehicle Type' },
  { key: 'vehicleDate', label: 'Date' },
  { key: 'vehicleInTime', label: 'Vehicle IN Time' },
  { key: 'vehicleOutTime', label: 'Vehicle OUT Time' },
  { key: 'driverName', label: 'Driver Name' },
  { key: 'driverContact', label: 'Driver Contact' },
  { key: 'quantityVerifiedByDispatch', label: 'Qty Verified By (Dispatch Staff)' },
  { key: 'qcVerifiedBy', label: 'Dispatch Confirmed By' }
];

const dispatchRequiredAttachments = [
  { key: 'vehicleProof', label: 'Vehicle Proof' },
  { key: 'deliveryChallanUrl', label: 'Delivery Challan' },
  { key: 'bomSignedUrl', label: 'BOM (Signed)' },
  { key: 'qcUploadUrl', label: 'QC Upload' }
];

const QualityCheckDetail = ({ db, user }) => {
  const { config } = useConfig();
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const canManageQc = isWarehouseUser(user);
  const [loading, setLoading] = useState(true);
  const [isEditing, setIsEditing] = useState(location.state?.mode === 'edit');
  const [isSaving, setIsSaving] = useState(false);
  const [uploading, setUploading] = useState({ vehicleProof: false, deliveryChallan: false, bomSigned: false, qcUpload: false });
  const [formData, setFormData] = useState(null);
  const [order, setOrder] = useState(null);
  const [dispatches, setDispatches] = useState([]);
  const printRef = useRef();
  const fieldStaffOptions = config?.field_staff?.length ? config.field_staff : (config?.qc_verified_by || []);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const docRef = doc(db, "dispatches", id);
        const docSnap = await getDoc(docRef);
        if (docSnap.exists()) {
          const data = docSnap.data();
          setFormData({
            ...data,
            id: docSnap.id,
            quantityVerifiedByDispatch: data.quantityVerifiedByDispatch || '',
            vehicleNumber: data.vehicleNumber || '',
            vehicleType: data.vehicleType || '',
            vehicleDate: data.vehicleDate || new Date().toISOString().split('T')[0],
            vehicleInTime: data.vehicleInTime || '',
            vehicleOutTime: data.vehicleOutTime || '',
            driverName: data.driverName || '',
            driverContact: data.driverContact || '',
            driverID: data.driverID || '',
            vehiclePhotoUrl: data.vehiclePhotoUrl || '',
            vehicleProofUrl: data.vehicleProofUrl || '',
            vehicleProofFiles: Array.isArray(data.vehicleProofFiles)
              ? data.vehicleProofFiles
              : (data.vehicleProofUrl ? [{ name: data.vehicleProofName || data.vehicleProofUrl.split('/').pop()?.split('?')[0] || 'Vehicle Proof', url: data.vehicleProofUrl }] : []),
            deliveryChallanUrl: data.deliveryChallanUrl || '',
            bomSignedUrl: data.bomSignedUrl || '',
            qcUploadUrl: data.qcUploadUrl || '',
            qcVerifiedBy: data.qcVerifiedBy || '',
            taxInvoiceUrl: data.taxInvoiceUrl || '',
            ewayBillUrl: data.ewayBillUrl || '',
            einvoiceUrl: data.einvoiceUrl || '',
            qcChecklist: data.qcChecklist || {
                qualityChecked: false,
                docsHanded: false,
                qtyVerified: false,
                safetyTag: false,
                informedHO: false,
                properPacking: false,
                laserMarking: false,
                stickersDone: false
            }
          });

          if (data.orderId) {
            const orderSnap = await getDoc(doc(db, "orders", data.orderId));
            if (orderSnap.exists()) {
              setOrder({ id: orderSnap.id, ...orderSnap.data() });
            }
          }
        } else {
          alert("MOC not found");
          navigate('/ops/quality-check');
        }
      } catch (error) {
        console.error("Error fetching DC:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [db, id, navigate]);

  // Real-time subscription to dispatches for the timeline
  useEffect(() => {
    if (!formData?.orderId) return;
    const q = query(collection(db, "dispatches"), where("orderId", "==", formData.orderId));
    const unsubscribe = onSnapshot(q, (snapshot) => {
      const dispatchesList = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setDispatches(dispatchesList);
    });
    return () => unsubscribe();
  }, [db, formData?.orderId]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleChecklistChange = (field) => {
    setFormData(prev => ({
        ...prev,
        qcChecklist: {
            ...prev.qcChecklist,
            [field]: !prev.qcChecklist[field]
        }
    }));
  };

  const getUploadFieldName = (type) => {
    if (type === 'vehicleProof') return 'vehicleProofUrl';
    if (type === 'deliveryChallan') return 'deliveryChallanUrl';
    if (type === 'qcUpload') return 'qcUploadUrl';
    return 'bomSignedUrl';
  };

  const getUploadValue = (type) => {
    if (type === 'vehicleProof') {
      return formData?.vehicleProofFiles || [];
    }

    const fieldName = getUploadFieldName(type);
    const url = formData?.[fieldName];
    if (!url) return [];
    return [{ name: url.split('/').pop()?.split('?')[0] || `${type}.pdf`, url }];
  };

  const handleFileUpload = async (file, type) => {
    if (!file) return;
    setUploading(prev => ({ ...prev, [type]: true }));
    try {
      const path = `qc_docs/${id}/${type}_${Date.now()}_${file.name}`;
      const storageRef = ref(storage, path);
      await uploadBytes(storageRef, file);
      const url = await getDownloadURL(storageRef);
      const fieldName = getUploadFieldName(type);
      setFormData(prev => ({ ...prev, [fieldName]: url }));
      await updateDoc(doc(db, "dispatches", id), { [fieldName]: url });
    } catch (err) {
      alert("Upload failed: " + err.message);
    } finally {
      setUploading(prev => ({ ...prev, [type]: false }));
    }
  };

  const handleVehicleProofFilesChange = async (files) => {
    const normalizedFiles = files || [];
    if (!normalizedFiles.length) {
      setFormData(prev => ({ ...prev, vehicleProofFiles: [], vehicleProofUrl: '' }));
      await updateDoc(doc(db, "dispatches", id), { vehicleProofFiles: [], vehicleProofUrl: '' });
      return;
    }

    const existingFiles = normalizedFiles
      .filter(file => file.url)
      .map(file => ({
        name: file.name || 'Vehicle Proof',
        url: file.url,
        size: file.size || 0,
        type: file.type || '',
        uploadedAt: file.uploadedAt || ''
      }));
    const newFiles = normalizedFiles.filter(file => !file.url);

    if (!newFiles.length) {
      const firstUrl = existingFiles[0]?.url || '';
      setFormData(prev => ({ ...prev, vehicleProofFiles: existingFiles, vehicleProofUrl: firstUrl }));
      await updateDoc(doc(db, "dispatches", id), { vehicleProofFiles: existingFiles, vehicleProofUrl: firstUrl });
      return;
    }

    setUploading(prev => ({ ...prev, vehicleProof: true }));
    try {
      const uploadedFiles = await Promise.all(newFiles.map(async (file, index) => {
        const path = `qc_docs/${id}/vehicleProof_${Date.now()}_${index}_${file.name}`;
        const storageRef = ref(storage, path);
        await uploadBytes(storageRef, file);
        const url = await getDownloadURL(storageRef);

        return {
          name: file.name,
          url,
          size: file.size || 0,
          type: file.type || '',
          uploadedAt: new Date().toISOString()
        };
      }));

      const nextFiles = [...existingFiles, ...uploadedFiles];
      const firstUrl = nextFiles[0]?.url || '';
      setFormData(prev => ({ ...prev, vehicleProofFiles: nextFiles, vehicleProofUrl: firstUrl }));
      await updateDoc(doc(db, "dispatches", id), {
        vehicleProofFiles: nextFiles,
        vehicleProofUrl: firstUrl,
        updatedAt: serverTimestamp()
      });
    } catch (err) {
      alert("Upload failed: " + err.message);
    } finally {
      setUploading(prev => ({ ...prev, vehicleProof: false }));
    }
  };

  const handleDocumentFilesChange = async (files, type) => {
    if (type === 'vehicleProof') {
      await handleVehicleProofFilesChange(files);
      return;
    }

    const fieldName = getUploadFieldName(type);
    if (!files?.length) {
      setFormData(prev => ({ ...prev, [fieldName]: '' }));
      await updateDoc(doc(db, "dispatches", id), { [fieldName]: '' });
      return;
    }

    const [file] = files;
    if (file?.url && !file?.size) {
      setFormData(prev => ({ ...prev, [fieldName]: file.url }));
      return;
    }

    await handleFileUpload(file, type);
  };

  const calculateFulfillment = async (orderId, currentMocId = null) => {
      const q = query(collection(db, "dispatches"), where("orderId", "==", orderId));
      const snap = await getDocs(q);
      const map = {};
      snap.docs.forEach(docSnap => {
          if (docSnap.id === currentMocId) return; 
          const data = docSnap.data();
          // We count items that are in progress or completed
          if (!['canceled', 'packing_rejected', 'fin_rejected'].includes(data.dispatchStatus)) {
              data.components?.forEach(c => {
                  if (!map[c.sku]) map[c.sku] = { new: 0, rental: 0 };
                  map[c.sku].new += (parseInt(c.newQty) || 0);
                  map[c.sku].rental += (parseInt(c.rentalQty) || 0);
              });
          }
      });
      return map;
  };

  const handleSaveDraft = async () => {
    setIsSaving(true);
    try {
      await updateDoc(doc(db, "dispatches", id), {
        ...formData,
        updatedAt: serverTimestamp()
      });
      alert("QC Draft Saved!");
      setIsEditing(false);
    } catch (error) {
      alert("Error saving draft: " + error.message);
    } finally {
      setIsSaving(false);
    }
  };

  const handleConfirmDispatch = async () => {
    const missingFields = dispatchRequiredFields
      .filter(field => !String(formData[field.key] || '').trim())
      .map(field => field.label);
    const hasVehicleProof = Boolean(formData.vehicleProofUrl || formData.vehicleProofFiles?.length);
    const missingAttachments = dispatchRequiredAttachments
      .filter(attachment => attachment.key === 'vehicleProof' ? !hasVehicleProof : !formData[attachment.key])
      .map(attachment => attachment.label);
    const missingRequirements = [...missingFields, ...missingAttachments];

    if (missingRequirements.length > 0) {
      alert(`Please complete all required QC dispatch fields before confirming:\n\n${missingRequirements.join('\n')}`);
      setIsEditing(true);
      return;
    }

    if (!window.confirm("Confirm final dispatch? This will move the truck to In Transit.")) {
      return;
    }

    setIsSaving(true);
    try {
      const timestamp = serverTimestamp();

      const updates = {
        ...formData,
        dispatchStatus: 'dispatched',
        dispatchedAt: timestamp,
        updatedAt: timestamp
      };

      // --- AUTOMATED EMAIL: DISPATCHED ---
      if (!formData.dispatchEmailSent) {
        const emailQueued = await sendEmailNotification(db, 'dispatched', updates, order);
        if (emailQueued) {
          updates.dispatchEmailSent = true;
        } else {
          console.warn(`Dispatch email was not queued for ${formData.mocNo}.`);
        }
      }

      // 1. Save form data AND update status to dispatched
      await updateDoc(doc(db, "dispatches", id), updates);

      // 2. Handle Split / Balance Logic
      if (formData.orderId) {
        const otherMocsFulfillment = await calculateFulfillment(formData.orderId, id);
        let anyRemaining = false;

        const remainingComponents = formData.components.map(c => {
          const prevMocd = (formData.typeOfMOC === 'Rental')
            ? (otherMocsFulfillment[c.sku]?.rental || 0)
            : (otherMocsFulfillment[c.sku]?.new || 0);

          const currentMocd = (formData.typeOfMOC === 'Rental') ? (c.rentalQty || 0) : (c.newQty || 0);
          const totalMocdSoFar = prevMocd + currentMocd;
          const balance = Math.max(0, (c.orderQty || 0) - totalMocdSoFar);

          if (balance > 0) {
            anyRemaining = true;
            return {
              ...c,
              newQty: formData.typeOfMOC === 'Rental' ? 0 : balance,
              rentalQty: formData.typeOfMOC === 'Rental' ? balance : 0,
              ticked: true
            };
          }
          return null;
        }).filter(c => c !== null);

        // If there's a balance, auto-create the NEXT draft MOC
        if (anyRemaining) {
          const nextMocNo = await getNextMocNumber(db);
          const draftPayload = {
            ...formData,
            mocNo: nextMocNo,
            mocDate: new Date().toISOString().split('T')[0],
            components: remainingComponents,
            dispatchStatus: 'moc_draft',
            updatedAt: timestamp,
            createdAt: timestamp,
          };
          delete draftPayload.id;
          await addDoc(collection(db, "dispatches"), draftPayload);
        }

        // Update Order Status
        await updateDoc(doc(db, "orders", formData.orderId), {
          status: anyRemaining ? 'partially_shipped' : 'in_transit',
          updatedAt: timestamp
        });
      }

      alert("Dispatch Confirmed!");
      navigate('/ops/in-transit');
    } catch (error) {
      console.error("Error confirming dispatch:", error);
      alert("Error: " + error.message);
    } finally {
      setIsSaving(false);
    }
  };

  const handleExportPDF = () => {
    const printContent = printRef.current.innerHTML;
    const printWindow = window.open('', '_blank');
    printWindow.document.write(`
      <html>
        <head>
          <title>MOC ${formData.mocNo}</title>
          <style>
            body { font-family: Arial, sans-serif; color: #000; margin: 0; padding: 0; }
            table { width: 100%; border-collapse: collapse; margin-bottom: 0px; }
            td, th { border: 1px solid #000 !important; padding: 8px; text-align: left; font-size: 12px; }
            @page { margin: 1cm; }
          </style>
        </head>
        <body>${printContent}</body>
      </html>
    `);
    printWindow.document.close();
    printWindow.focus();
    setTimeout(() => { printWindow.print(); printWindow.close(); }, 250);
  };

  const renderEditForm = () => (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1.5fr', gap: '20px' }}>
            {/* QC Checklist */}
            <div style={cardStyle}>
                <h3 style={sectionHeaderStyle}><ClipboardCheck size={18} /> QC Checklist</h3>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                    {[
                      ...qcChecklistItems
                    ].map(item => (
                        <label key={item.key} style={{ display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer', fontSize: '14px' }}>
                            <input type="checkbox" checked={formData.qcChecklist[item.key]} onChange={() => handleChecklistChange(item.key)} style={{ width: '18px', height: '18px' }} />
                            {item.label}
                        </label>
                    ))}
                </div>
            </div>

            {/* Logistics Info */}
            <div style={cardStyle}>
                <h3 style={sectionHeaderStyle}><Truck size={18} /> Logistics & Vehicle Info</h3>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: '15px' }}>
                    <div>
                        <label style={labelStyle}>Vehicle Type</label>
                        <select name="vehicleType" value={formData.vehicleType} onChange={handleChange} style={inputStyle}>
                            <option value="">Select Type...</option>
                            {config.vehicle_types?.map(type => <option key={type} value={type}>{type}</option>)}
                        </select>
                    </div>
                    <div><label style={labelStyle}>Date</label><input type="date" name="vehicleDate" value={formData.vehicleDate} onChange={handleChange} style={inputStyle} /></div>
                    <div style={{ gridColumn: 'span 3', display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: '15px' }}>
                        <div><label style={labelStyle}>Vehicle IN Time</label><input type="time" name="vehicleInTime" value={formData.vehicleInTime} onChange={handleChange} style={inputStyle} /></div>
                        <div><label style={labelStyle}>Vehicle OUT Time</label><input type="time" name="vehicleOutTime" value={formData.vehicleOutTime} onChange={handleChange} style={inputStyle} /></div>
                    </div>
                    <div style={{ gridColumn: 'span 3', display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: '15px' }}>
                        <div><label style={labelStyle}>Driver Name</label><input name="driverName" value={formData.driverName} onChange={handleChange} style={inputStyle} /></div>
                        <div><label style={labelStyle}>Driver Contact</label><input name="driverContact" value={formData.driverContact} onChange={handleChange} style={inputStyle} /></div>
                    </div>
                </div>
            </div>
        </div>

        <div style={cardStyle}>
            <h3 style={sectionHeaderStyle}><FileText size={18} /> Dispatch Attachments</h3>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, minmax(0, 1fr))', gap: '20px' }}>
                <div style={{ minWidth: 0 }}>
                    <label style={labelStyle}>Vehicle Proof</label>
                    <MultiFileUpload
                      value={getUploadValue('vehicleProof')}
                      onFilesChange={(files) => handleDocumentFilesChange(files, 'vehicleProof')}
                      multiple={true}
                      maxSizeMB={25}
                      helperText="Upload one or more vehicle proof files."
                      uploading={uploading.vehicleProof}
                    />
                </div>
                <div style={{ minWidth: 0 }}>
                    <label style={labelStyle}>Delivery Challan</label>
                    <MultiFileUpload
                      value={getUploadValue('deliveryChallan')}
                      onFilesChange={(files) => handleDocumentFilesChange(files, 'deliveryChallan')}
                      multiple={false}
                      maxFiles={1}
                      maxSizeMB={25}
                      allowedTypes={['application/pdf']}
                      helperText="Upload or replace the signed delivery challan in PDF format."
                      uploading={uploading.deliveryChallan}
                    />
                </div>
                <div style={{ minWidth: 0 }}>
                    <label style={labelStyle}>BOM (Signed)</label>
                    <MultiFileUpload
                      value={getUploadValue('bomSigned')}
                      onFilesChange={(files) => handleDocumentFilesChange(files, 'bomSigned')}
                      multiple={false}
                      maxFiles={1}
                      maxSizeMB={25}
                      helperText="Upload or replace the signed BOM."
                      uploading={uploading.bomSigned}
                    />
                </div>
                <div style={{ minWidth: 0 }}>
                    <label style={labelStyle}>QC Upload</label>
                    <MultiFileUpload
                      value={getUploadValue('qcUpload')}
                      onFilesChange={(files) => handleDocumentFilesChange(files, 'qcUpload')}
                      multiple={false}
                      maxFiles={1}
                      maxSizeMB={25}
                      helperText="Upload or replace the QC file."
                      uploading={uploading.qcUpload}
                    />
                </div>
            </div>
        </div>

        {/* Verification */}
        <div style={cardStyle}>
            <h3 style={sectionHeaderStyle}><UserCheck size={18} /> Final Verification</h3>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
                <div>
                    <label style={labelStyle}>Qty Verified By (Dispatch Staff)</label>
                    <select name="quantityVerifiedByDispatch" value={formData.quantityVerifiedByDispatch} onChange={handleChange} style={inputStyle}>
                        <option value="">Select Staff...</option>
                        {fieldStaffOptions.map(opt => <option key={opt} value={opt}>{opt}</option>)}
                    </select>
                </div>
                <div>
                    <label style={labelStyle}>Dispatch Confirmed By</label>
                    <select name="qcVerifiedBy" value={formData.qcVerifiedBy} onChange={handleChange} style={inputStyle}>
                        <option value="">Select Name...</option>
                        {fieldStaffOptions.map(opt => <option key={opt} value={opt}>{opt}</option>)}
                    </select>
                </div>
            </div>
        </div>
    </div>
  );

  if (loading) return <Spinner />;

  return (
    <div style={{ maxWidth: '1100px', margin: '0 auto', padding: '20px' }}>
      <div style={toolbarStyle}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '15px', flexWrap: 'wrap' }}>
          <button onClick={() => navigate('/ops/quality-check')} style={backBtnStyle}><ArrowLeft size={24} /></button>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
            <h2 style={{ margin: 0 }}>QC Gate: {formData.mocNo}</h2>
            {formData.dispatchStatus === 'ewaybill_complete' && (
              <StatusBadge status="ewaybill_complete" />
            )}
            {formData.dispatchStatus === 'dispatched' && (
              <StatusBadge status="dispatched" />
            )}
          </div>
        </div>
        <div style={{ display: 'flex', gap: '10px' }}>
          {isEditing && canManageQc ? (
            <>
                <button onClick={() => setIsEditing(false)} style={secondaryBtnStyle}>Cancel</button>
                <button onClick={handleSaveDraft} disabled={isSaving} style={draftBtnStyle}><Save size={18} /> Save Draft</button>
                <button onClick={handleConfirmDispatch} disabled={isSaving} style={confirmBtnStyle}><Truck size={18} /> Confirm Dispatch</button>
            </>
          ) : (
            <>
                {canManageQc && formData.dispatchStatus !== 'dispatched' && (
                    <>
                        <button onClick={() => setIsEditing(true)} style={editBtnStyle}><Edit2 size={18} /> Edit QC</button>
                        <button onClick={handleConfirmDispatch} disabled={isSaving} style={confirmBtnStyle}><Truck size={18} /> Confirm Dispatch</button>
                    </>
                )}
                <button onClick={handleExportPDF} style={secondaryBtnStyle}><Printer size={18} /> Export PDF</button>
            </>
          )}
        </div>
      </div>
      {isEditing && canManageQc ? renderEditForm() : (
        <QCPreview
          formData={formData}
          printRef={printRef}
          onConfirmDispatch={canManageQc ? handleConfirmDispatch : undefined}
          isSaving={isSaving}
        />
      )}
      {/* FOOTER TIMELINE */}
      {!loading && formData && !isEditing && (
        <div style={{ 
          marginTop: '40px', 
          maxWidth: '1100px', 
          margin: '50px auto 20px auto',
          animation: 'fadeIn 0.5s ease-in-out'
        }}>
          <h3 style={{ 
            fontSize: '16px', 
            fontWeight: 'bold', 
            color: '#374151', 
            marginBottom: '15px',
            display: 'flex',
            alignItems: 'center',
            gap: '8px'
          }}>
            <Clock size={18} color="#2563eb" /> Order Timeline
          </h3>
          <HorizontalTimeline orderData={order} dispatchesData={dispatches} dispatchData={formData} />
        </div>
      )}
    </div>
  );
};

// --- STYLES ---
const toolbarStyle = { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '25px', backgroundColor: 'white', padding: '15px', borderRadius: '10px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' };
const backBtnStyle = { background: 'none', border: 'none', cursor: 'pointer', color: '#64748b' };
const btnStyle = { padding: '10px 20px', borderRadius: '8px', fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', border: 'none' };
const confirmBtnStyle = { ...btnStyle, backgroundColor: '#059669', color: 'white' };
const draftBtnStyle = { ...btnStyle, backgroundColor: 'white', color: '#374151', border: '1px solid #d1d5db' };
const editBtnStyle = { ...btnStyle, backgroundColor: '#2563eb', color: 'white' };
const secondaryBtnStyle = { ...btnStyle, backgroundColor: 'white', border: '1px solid #d1d5db', color: '#374151' };

const cardStyle = { backgroundColor: 'white', padding: '25px', borderRadius: '12px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' };
const sectionHeaderStyle = { fontSize: '16px', fontWeight: 'bold', borderBottom: '1px solid #f3f4f6', paddingBottom: '10px', marginBottom: '15px', color: '#1e293b', display: 'flex', alignItems: 'center', gap: '8px' };
const labelStyle = { display: 'block', fontSize: '11px', fontWeight: '700', color: '#64748b', marginBottom: '6px', textTransform: 'uppercase' };
const inputStyle = { width: '100%', padding: '10px', borderRadius: '6px', border: '1px solid #d1d5db', fontSize: '14px', boxSizing: 'border-box' };

export default QualityCheckDetail;
